import { css } from '@compiled/react';
import { defineMessage, useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';
import { MotionContainer } from '@/patterns/global-nav/src/components/motion-container';
import { GlobalNavDropdownLinkColumn } from '@/patterns/global-nav/src/layouts/global-nav-dropdown-link-column';
import { RowGrid } from '@/patterns/global-nav/src/layouts/row-grid';
import { DivOnHoverExit } from '@/patterns/global-nav/src/nav-links-container';

function useWhyBitbucketLinks() {
	return {
		features: {
			id: 'solution',
			title: defineMessage({
				id: 'bitbucketnav.subnav.whybitbucket.links.solution',
				defaultMessage: 'Solution',
			}),
			items: [
				{
					id: 'ci-cd',
					text: defineMessage({
						id: 'bitbucketnav.subnav.whybitbucket.links.work-management',
						defaultMessage: 'CI/CD',
					}),
					href: useLocalizedHref('/software/bitbucket/features/pipelines'),
				},
				{
					id: 'packages',
					text: defineMessage({
						id: 'bitbucketnav.subnav.whybitbucket.links.packages',
						defaultMessage: 'Packages',
					}),
					href: useLocalizedHref('/software/bitbucket/features/packages'),
				},
				{
					id: 'cloud-security',
					text: defineMessage({
						id: 'bitbucketnav.subnav.whybitbucket.links.cloud-security',
						defaultMessage: 'Cloud Security',
					}),
					href: useLocalizedHref('/software/bitbucket/features/cloud-security'),
				},
				{
					id: 'dev-sec-ops',
					text: defineMessage({
						id: 'bitbucketnav.subnav.whybitbucket.links.dev-sec-ops',
						defaultMessage: 'DevSecOps',
					}),
					href: useLocalizedHref('/software/bitbucket/features/devsecops'),
				},
				{
					id: 'code-review',
					text: defineMessage({
						id: 'bitbucketnav.subnav.whybitbucket.links.it-service-management',
						defaultMessage: 'Code Review',
					}),
					href: useLocalizedHref('/software/bitbucket/features/code-review'),
				},
				{
					id: 'jira-integrations',
					text: defineMessage({
						id: 'bitbucketnav.subnav.whybitbucket.links.agile-devops',
						defaultMessage: 'Jira Integrations',
					}),
					href: useLocalizedHref('/software/jira/bitbucket-integration'),
				},
			],
		},
		plans: {
			id: 'plans',
			title: defineMessage({
				id: 'bitbucketnav.subnav.whybitbucket.links.plans',
				defaultMessage: 'Plans',
			}),
			items: [
				{
					id: 'bitbucket-cloud-premium',
					text: defineMessage({
						id: 'bitbucketnav.subnav.whybitbucket.links.bitbucket-cloud-premium',
						defaultMessage: 'Bitbucket Cloud Premium',
					}),
					href: useLocalizedHref('/software/bitbucket/premium'),
				},
			],
		},
	} as const;
}

export const WhyBitbucketDropdown = ({
	productKey = 'bitbucket',
}: {
	productKey?: ProductKeyTypes;
}) => {
	const intl = useIntl();

	const bitbucketLinks = useWhyBitbucketLinks();
	const linkColumns = Object.values(bitbucketLinks);

	return (
		<DivOnHoverExit>
			<TypographyStyles>
				<MotionContainer direction="up">
					<div
						css={css({
							backgroundColor: wacToken('color.neutral.light100'),
						})}
					>
						<RowGrid
							numCols={2}
							columnGap={token('space.500')}
							paddingBlock={token('space.500')}
							paddingInline={token('space.500')}
						>
							{linkColumns.map((column) => {
								return (
									<GlobalNavDropdownLinkColumn key={column.id}>
										<span
											css={css({
												textTransform: 'uppercase',
												color: wacToken('color.primary.black'),
												fontSize: '0.75rem',
												fontFamily: wacToken('font.charlie.text'),
												fontWeight: 700,
												lineHeight: '1rem',
											})}
										>
											{intl.formatMessage(column.title)}
										</span>
										<div
											css={css({
												display: 'grid',
												gridTemplateRows: '40px 40px 40px 40px 40px',
											})}
										>
											{column.items.map((link) => {
												return (
													<div
														key={link.id}
														css={css({
															// nav doesn't follow the typography
															// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
															a: {
																fontWeight: 400,
																fontSize: '0.875rem',
																color: wacToken('color.primary.black'),
																'&:visited': {
																	textDecoration: 'none',
																	color: wacToken('color.primary.black'),
																},
																'&:hover, &:focus': {
																	color: wacToken('color.extended.blue800'),
																},
															},
															display: 'flex',
															alignItems: 'end',
														})}
													>
														{/* eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
														<Link
															type="anchor"
															href={link.href}
															text={intl.formatMessage(link.text)}
															ariaLabel={intl.formatMessage(link.text)}
															dataEventProps={{
																'data-event': 'clicked',
																'data-event-component': 'linkButton',
																'data-event-container': 'globalNavSubnav',
																'data-product-key': productKey,
															}}
														/>
													</div>
												);
											})}
										</div>
									</GlobalNavDropdownLinkColumn>
								);
							})}
						</RowGrid>
					</div>
				</MotionContainer>
			</TypographyStyles>
		</DivOnHoverExit>
	);
};
